import CachedIcon from "@mui/icons-material/Cached";
import ClearIcon from "@mui/icons-material/Clear";
import CloudDownload from "@mui/icons-material/CloudDownload";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseDialog from "components/CloseDialog";
import {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import LazyTable from "components/LazyTable";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  ListRequestOfStateFilter,
  ListRequestOfStateFilterRequest,
  SkuRegistration,
  StateFilter,
  StateFilterRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { convertToZonedDate } from "utils/dateUtils";
import MoveSkuRegistrationsDialog from "./MoveSkuRegistrationsDialog";

type UserSkusTableProps = {
  skuId: string;
};

interface ISkuRegistrationRow extends SkuRegistration, LazyTableRow {
  id: string;
}

export const SkuRegistrationsList = ({ skuId }: UserSkusTableProps) => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    userSkuService,
    userCourseUrl,
    skuRegistrationsDownloadUrl,
  } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("lastName");
  const [dialogOpener, dialogOpenerActions] = useBoolean(false);
  const [moveDialogOpen, moveDialogActions] = useBoolean(false);

  const [skuRegistrationStateId, setSkuRegistrationStateId] = useState<
    string | null | undefined
  >(null);

  const searchTerm = persistanceStore.useState(
    (s) => s.skuRegistrationsSearchTerm,
  );

  const { organizationId } = contextStore.useState();

  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const fetchedUserSkuStates = useQuery(["userSkuStates"], () =>
    userSkuService.getUserSkuStates(ListRequestOfBaseFilter.fromJS({})),
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(["sku-registrations", skuId]);
  }, [queryClient, skuId]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    // Re-query when sorting changes
    refresh();
  }, [order, orderBy, refresh]);

  const { isError, data, error, isFetching } = useQuery(
    [
      "sku-registrations",
      skuId,
      debouncedSearchTerm,
      page,
      rowsPerPage,
      order,
      orderBy,
      skuRegistrationStateId,
      organizationId,
    ],
    () =>
      userSkuService.getSkuRegistrations(
        skuId,
        ListRequestOfStateFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: StateFilter.fromJS({
            searchText: debouncedSearchTerm,
            stateId: skuRegistrationStateId,
          }),
        }),
      ),
    {
      keepPreviousData: true,
      // Ensure React Query doesn't use stale data for sorting
      staleTime: 0,
    },
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const basicCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...basicCell,
      accessor: "firstName",
      label: t({ id: "firstNameLabel" }),
      customSortAccessor: "firstName",
    },
    {
      ...basicCell,
      accessor: "lastName",
      label: t({ id: "lastNameLabel" }),
      customSortAccessor: "lastName",
    },
    {
      ...basicCell,
      accessor: "email",
      label: t({ id: "emailLabel" }),
      customSortAccessor: "email",
    },
    {
      ...basicCell,
      accessor: "registrationDate",
      type: "date",
      label: t({ id: "registrationDateLabel" }),
      customSortAccessor: "registrationDate",
    },
    {
      ...basicCell,
      accessor: "completionDate",
      type: "date",
      label: t({ id: "completionDateLabel" }),
      customSortAccessor: "completionDate",
    },
    {
      ...basicCell,
      accessor: "status",
      label: t({ id: "statusLabel" }),
      customSortAccessor: "status",
    },
    {
      accessor: "",
      align: "center",
      width: 0,
      type: "node",
      label: t({ id: "manageLabel" }),
      View: ({ value }: { value: SkuRegistration }) => {
        return (
          <Tooltip title={t({ id: "manage" })}>
            <IconButton
              size="small"
              style={{ color: "darkblue" }}
              onClick={() =>
                navigate(userCourseUrl(value.userId, value.userSkuId))
              }
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        );
      },
      noSort: true,
    },
  ];

  const skuRegistrations = (data?.data ?? []).map(
    (sr) =>
      ({
        ...sr,
        id: sr.userSkuId,
        registrationDate: convertToZonedDate(sr?.registrationDate),
        completionDate: convertToZonedDate(sr?.completionDate),
      } as ISkuRegistrationRow),
  );

  const exportRegistrations = async () => {
    dialogOpenerActions.setTrue();
    await userSkuService.exportSkuRegistrations(
      skuId,
      ListRequestOfStateFilterRequest.fromJS({
        parameters: StateFilterRequest.fromJS({
          skuRegistrationsDownloadUrl: skuRegistrationsDownloadUrl(skuId, ""),
          stateFilter: StateFilter.fromJS({
            searchText: debouncedSearchTerm,
            stateId: skuRegistrationStateId,
          }),
        }),
      }),
    );
  };

  return (
    <>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={11} sm={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.skuRegistrationsSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e: any) =>
                persistanceStore.update((s) => {
                  s.skuRegistrationsSearchTerm = e.target.value;
                })
              }
              data-cy="searchText"
            />
          </Grid>
          <Grid item xs={11} sm={12} md={3}>
            <SelectEntity
              value={skuRegistrationStateId ?? ""}
              fetchedData={fetchedUserSkuStates}
              labelField="value"
              label={t({ id: "statusLabel" })}
              onChange={(id) => setSkuRegistrationStateId(id)}
              textFieldProps={{
                variant: "outlined",
              }}
            />
          </Grid>
          <Grid item xs={11} sm={6} md={2}>
            <Button
              type="submit"
              color="primary"
              variant="outlined"
              startIcon={<CloudDownload />}
              onClick={exportRegistrations}
              style={{ height: "100%" }}
            >
              <T id="exportRegistrations" />
            </Button>
          </Grid>

          <Grid item xs={11} sm={6} md={2}>
            <Button
              type="button"
              color="primary"
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={moveDialogActions.setTrue}
              style={{ height: "100%" }}
            >
              <T id="moveSkuRegistrations" />
            </Button>
          </Grid>

          <Grid item xs={11} sm={12} md={1}>
            <Tooltip
              title={t({
                id: "refresh",
              })}
            >
              <IconButton onClick={refresh}>
                <CachedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: skuRegistrations,
          loading: isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />
      <CloseDialog
        dialogTitleKey="exportProcessing"
        contentKey="exportProcessingMessage"
        open={dialogOpener}
        onClose={dialogOpenerActions.setFalse}
      />
      <MoveSkuRegistrationsDialog
        sourceSkuId={skuId}
        open={moveDialogOpen}
        close={moveDialogActions.setFalse}
      />
    </>
  );
};

export default SkuRegistrationsList;
